// src/components/desktop/navbar/DesktopNavbar.jsx
import "./DesktopNavbar.css";
import logoUrl from "../../../assets/brand/logo.svg";
import { useLang } from "../../../i18n/LangContext";
import { useEffect, useMemo, useRef, useState } from "react";

export default function DesktopNavbar(props) {
  const { lang, toggleLang, t } = useLang();
  const navHeight = typeof props.navHeight === "number" ? props.navHeight : 88;

  // праг за появата на фон
  const stickyAt = typeof props.stickyAt === "number" ? props.stickyAt : 10;

  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState("");

  const routerMode = typeof props.onSelectSection === "function";

  // секциите, ако не са подадени отвън – fallback от t.nav
  const sections = useMemo(() => {
    const fb = [
      { id: "home", label: t.nav.home },
      { id: "advertise", label: t.nav.advertise },
      { id: "monetize", label: t.nav.monetize },
      { id: "faq", label: t.nav.faq },
      { id: "about", label: t.nav.about },
    ];


    const list =
      Array.isArray(props.sections) && props.sections.length ? props.sections : fb;
    // първоначално активна – първата секция
    if (!activeId && list[0]) setActiveId(list[0].id);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.sections, props.isAuthenticated, t.nav]);

  // ако родителят ни подава activeId (router режим) – синхронизираме
  useEffect(() => {
    if (props.activeId && props.activeId !== activeId) {
      setActiveId(props.activeId);
    }
  }, [props.activeId, activeId]);

  // кеш от позиции на секциите (реф, за да не се създава нов масив на всеки рендер)
  const positionsRef = useRef([]);

  // измерване на позициите
  function measurePositions() {
    positionsRef.current = sections
      .map((s) => {
        const el = document.getElementById(s.id);
        if (!el) return null;
        // абсолютен Y на секцията спрямо документа, минус навбара и малък offset
        const top = window.pageYOffset + el.getBoundingClientRect().top - (navHeight + 16);
        return { id: s.id, top };
      })
      .filter(Boolean);
  }

  // sticky фон + активна секция по скрол (само в scroll режим)
  useEffect(() => {
    if (routerMode) {
      // в router режим ни трябва само sticky фон
      function onScroll() {
        const y = window.scrollY;
        setScrolled(y > stickyAt);
      }

      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });

      return () => {
        window.removeEventListener("scroll", onScroll);
      };
    }

    function onScroll() {
      const y = window.scrollY;
      setScrolled(y > stickyAt);

      // определяме текущата секция: най-големият top <= y
      let current = positionsRef.current?.[0]?.id || activeId;
      for (const p of positionsRef.current) {
        if (y >= p.top) current = p.id;
        else break; // позициите са по ред – можем да спрем
      }
      if (current && current !== activeId) setActiveId(current);
    }

    function onResize() {
      measurePositions();
      onScroll();
    }

    // първо измерване след монтване
    measurePositions();
    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    // понякога изображения променят височини:
    const tId = setTimeout(onResize, 300);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      clearTimeout(tId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, navHeight, stickyAt, routerMode, activeId]);

  function handleSectionClick(id) {
    // router режим: казваме на родителя и само подсветяваме
    if (routerMode) {
      props.onSelectSection(id);
      setActiveId(id);
      return;
    }

    // старият scroll режим
    const el = document.getElementById(id);
    if (!el) return;
    window.scrollTo({
      top: window.pageYOffset + el.getBoundingClientRect().top - navHeight,
      behavior: "smooth",
    });
    setActiveId(id); // моментално подсветяване
  }

  return (
    <header className={`nav-root ${scrolled ? "is-stuck" : ""}`} role="banner">
      <div className="nav-wrap">
        {/* Лого вляво */}
        <a className="brand" href="/" aria-label="Aura Home">
          <img src={logoUrl} alt="Aura" className="brand-logo" />
        </a>

        {/* Линкове */}
        {!props.hideLinks && (
          <nav className="nav-links" aria-label="Primary">
            {sections.map((section) => (
              <span
                key={section.id}
                className={`nav-link ${activeId === section.id ? "is-active" : ""}`}
                onClick={() => handleSectionClick(section.id)}
              >
                {section.label}
              </span>
            ))}
          </nav>
        )}

        {/* Дясно: Login */}
        <div className="nav-right">
          {props.isAuthenticated ? (
            <>
              <button
                type="button"
                className="login-btn nav-btn-secondary"
                onClick={props.onDashboard || (() => {})}
                aria-label="Dashboard"
                title="Dashboard"
              >
                {(t.nav.dashboard || "Dashboard").toUpperCase()}
              </button>
              <button
                type="button"
                className="login-btn"
                onClick={props.onLogout || (() => {})}
                aria-label="Logout"
                title="Logout"
              >
                {(t.nav.logout || "Logout").toUpperCase()}
              </button>
            </>
          ) : (
            <>
              {props.onRegister && (
                <button
                  type="button"
                  className="login-btn nav-btn-secondary"
                  onClick={props.onRegister}
                  aria-label="Register"
                  title="Register"
                >
                  {(t?.registerForm?.eyebrow || "Register").toUpperCase()}
                </button>
              )}
              <button
                type="button"
                className="login-btn"
                onClick={props.onLogin?.bind(null) || (() => {})}
                aria-label="Log in"
                title="Log in"
              >
                {(t.nav.login || "Log in").toUpperCase()}
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
